import { useState, useRef, useEffect } from 'react';
import eventService from '../services/eventService';
import { setEarlyBirdGrant } from '../utils/registration';

/**
 * Modal that prompts the attendee for the early bird registration password.
 *
 * On successful verification the backend returns a short-lived JWT grant
 * token which is stored in sessionStorage (per-event) and forwarded to
 * the caller via `onSuccess(token)`.
 *
 * Props:
 *   eventId   - numeric event ID
 *   eventName - human-readable event name (shown in the heading)
 *   onSuccess - callback(token) invoked after password is verified
 *   onCancel  - callback() invoked when the user dismisses the modal
 */
export default function EarlyBirdPasswordModal({ eventId, eventName, onSuccess, onCancel }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Focus the password input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Dismiss on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || submitting) return;

    try {
      setSubmitting(true);
      setError('');
      const result = await eventService.verifyEarlyBirdPassword(eventId, password);
      setEarlyBirdGrant(eventId, result.token);
      onSuccess(result.token);
    } catch (err) {
      setError(
        err.response?.data?.error?.message || 'Incorrect password'
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Early Bird Registration"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal card */}
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-stone-900 text-center mb-1">
          Early Bird Registration
        </h3>
        <p className="text-sm text-stone-500 text-center mb-6">
          Enter the access password to continue.
        </p>

        {/* Password field */}
        <div className="mb-4">
          <label htmlFor="early-bird-password" className="block text-sm font-medium text-stone-700 mb-1">
            Password
          </label>
          <input
            ref={inputRef}
            id="early-bird-password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            aria-label="Early bird password"
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 ${
              error ? 'border-red-300' : 'border-stone-200'
            }`}
            placeholder="Enter early bird password"
            autoComplete="off"
            disabled={submitting}
          />
          {error && (
            <p className="text-red-600 text-xs mt-1.5">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded-lg transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            className="px-5 py-2 text-sm font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </form>
    </div>
  );
}
